import { useEffect, useCallback, useMemo } from "react"
import { useAccountAlias, AI_MODULE_LIMIT } from "resources/account"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { useModules } from "../../context/modules"
import { MODULE_TYPES } from "../../modules/types"
import { DEFAULT_ARTIFACT_TITLES_BY_KIND } from "../../modules/ai-artifact/constants"
import type { Artifact, ArtifactKind } from "../../modules/ai-artifact/types"
import { usePinWorkflow } from "connected-components/ai-teammate/hooks/use-pin-workflow"
import { log } from "helpers/logger"
import { getErrorMessage } from "helpers/dev-logger"
import { getPinnedArtifactIds, getUniqueModuleTitle, isSuccessfullyPinnedArtifactModule } from "./helpers"

/**
 * Hook that listens for artifact pin requests from AI Teammate and handles them
 */
interface PinRequestPayload {
  artifact: Artifact
  kind: ArtifactKind
  title?: string
}

export function usePinArtifactHandler() {
  const { modules, isPreferencesLoaded, addModule, updateModule } = useModules()
  const aiModuleCount = useMemo(
    () => modules.filter((module) => module.type === MODULE_TYPES.AI_ARTIFACT).length,
    [modules],
  )
  const pinnedArtifactIds = useMemo(() => getPinnedArtifactIds(modules), [modules])
  const { mutateAsync: pinWorkflow } = usePinWorkflow()
  const accountAlias = useAccountAlias()

  const handlePinRequest = useCallback(async ({ artifact, kind, title }: PinRequestPayload) => {
    const existingModule = modules.find(
      (module) =>
        isSuccessfullyPinnedArtifactModule(module)
        && module.config?.artifactId === artifact.id,
    )
    if (existingModule) {
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId: existingModule.id,
        pinnedId: existingModule.config?.pinnedId ?? "",
        kind,
      })
      return
    }

    if (aiModuleCount >= AI_MODULE_LIMIT) {
      const errorMessage = `AI module limit of ${AI_MODULE_LIMIT} reached`
      log(`Cannot pin artifact: ${errorMessage}`)
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        kind,
      })
      return
    }

    let moduleId: string | undefined

    try {
      const resolvedTitle = getUniqueModuleTitle(
        title || DEFAULT_ARTIFACT_TITLES_BY_KIND[kind],
        modules,
      )

      // Add module in pinning state and wait for backend-generated pinnedId.
      moduleId = addModule({
        type: MODULE_TYPES.AI_ARTIFACT,
        config: {
          artifactId: artifact.id,
          kind,
          title: resolvedTitle,
          isPinning: true,
        },
      })

      // Call API to pin workflow - backend generates and returns pinnedId
      const result = await pinWorkflow({
        accountAlias,
        artifactId: artifact.id,
        title: resolvedTitle,
      })

      // Store backend-generated pinnedId in config
      updateModule(moduleId, {
        config: {
          artifactId: artifact.id,
          kind,
          title: resolvedTitle,
          pinnedId: result.pinnedId,
          isPinning: false,
        },
      })

      // Emit success event (for analytics, notifications, etc.)
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId,
        pinnedId: result.pinnedId,
        kind,
      })
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      log(`Failed to pin artifact: ${errorMessage}`)

      if (moduleId) {
        updateModule(moduleId, {
          config: {
            isPinning: false,
            hasError: true,
          },
        })
      }

      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        moduleId,
        kind,
      })
    }
  }, [modules, aiModuleCount, addModule, updateModule, pinWorkflow, accountAlias])

  // Broadcast AI module limit status once preferences are loaded and whenever count, limit, or pinned artifact IDs change.
  useEffect(() => {
    if (!isPreferencesLoaded) return

    dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_LIMIT_STATUS, {
      count: aiModuleCount,
      isLimitReached: aiModuleCount >= AI_MODULE_LIMIT,
      pinnedArtifactIds,
    })
  }, [isPreferencesLoaded, aiModuleCount, pinnedArtifactIds])

  useEffect(() => {
    return dashboardEvents.on<PinRequestPayload>(
      DASHBOARD_EVENTS.ARTIFACT_PIN_REQUESTED,
      handlePinRequest,
    )
  }, [handlePinRequest])
}
